"use client";
import React, { useState } from "react";
import { Box, Button, HStack, IconButton, VStack, Stack, Text } from "@chakra-ui/react";
import {
  MdArrowBackIos,
  MdArrowForwardIos,
} from "@onemind-services-llc/react-icons-ng/md";
import Image from "next/image";
import Link from "next/link";
import { Carousel } from "@onemind-services-llc/ui-components";
import { BUTTON_VARIANTS } from "@/theme/components/button";
import { useCustomerCarousel } from "@/hooks/useCustomCarousel";
import { MAIN_ROUTES } from "@/constants/routes";
import SecAwardsLogo from "public/home/secAwardsLogo.png";
import BgImage from "public/home/achievementsBg.png";
import { SectionDivider } from "../SectionDivider";
import { ACHIEVEMENTS_LIST } from "./constants";

type Props = { title: string; desc: string };

const AchievementCard = (props: Props) => {
  const { title, desc } = props;
  return (
    <VStack spacing={6}>
      <Image src={SecAwardsLogo} alt="Achievement Logo" width={200} height={200} />
      <Stack spacing={1}>
        <Box>
          <Text
            as="span"
            fontWeight={"normal"}
            textAlign={"center"}
            fontSize={{
              base: "2xl",
              sm: "3xl",
            }}
          >
            CloudMyLab wins
          </Text>
          <Text
            as="span"
            fontWeight={700}
            textAlign={"center"}
            fontSize={{
              base: "2xl",
              sm: "3xl",
            }}
          >
            {title}
          </Text>
        </Box>
        <Text fontSize="18px" color="gray.600" textAlign={"center"}>
          - The Security Awards 2025
        </Text>
      </Stack>
      <Box
        p={8}
        mx={{ md: 16 }}
        border="1px solid black"
        maxW="6xl"
        fontSize={"lg"}
        borderRadius={8}
        boxShadow={"lg"}
      >
        <Text fontSize={"lg"}>{desc}</Text>
        <Text fontWeight={600} mt={2}>
          - Annabella Whittall
        </Text>
        <Text fontStyle={"italic"}>COO , The Cloud Awards</Text>
      </Box>
      <Button
        as={Link}
        href={`${MAIN_ROUTES.ABOUT_US}?section=sec-achievements`}
        variant={BUTTON_VARIANTS.PRIMARY}
        mb={{ md: 4 }}
        minW={"200px"}
      >
        Learn More About Us
      </Button>
    </VStack>
  );
};

const AchievementComp = () => {
  const [carouselActions, setCarouselActions] = useState({
    slickPrev: () => {},
    slickNext: () => {},
  });

  const { slickPrev, slickNext } = carouselActions;
  const { settings } = useCustomerCarousel({
    dots: true,
    autoplay: false,
    slidesToShow: 1,
  });

  return (
    <Stack data-cy={"section-customers"} spacing={0}>
      <SectionDivider label="Achievements" />
      <VStack spacing={4} mt={"-2rem"} position={"relative"}>
        <Box
          w="100%"
          backgroundImage={`url(${BgImage.src})`}
          backgroundSize="cover"
          backgroundPosition="center"
          backgroundRepeat="no-repeat"
        >
          <Carousel settings={settings} setSliderRef={setCarouselActions}>
            {ACHIEVEMENTS_LIST.map((achievement, index) => (
              <Box key={index} px="1rem">
                <AchievementCard {...achievement} />
              </Box>
            ))}
          </Carousel>
        </Box>
        <HStack
          spacing={6}
          display={{ base: "none", md: "flex" }}
          position="absolute"
          top={"55%"}
          left="0"
          right="0"
          justifyContent="space-between"
          px={4}
          zIndex="2"
        >
          <IconButton
            aria-label="previous"
            fontSize="30px"
            icon={<MdArrowBackIos />}
            onClick={slickPrev}
            variant="ghost"
            _hover={{ bg: "transparent" }}
          />
          <IconButton
            aria-label="next"
            fontSize="30px"
            icon={<MdArrowForwardIos />}
            onClick={slickNext}
            variant="ghost"
            _hover={{ bg: "transparent" }}
          />
        </HStack>

        <HStack
          spacing={6}
          display={{ base: "flex", md: "none" }}
          justifyContent="center"
          mt={6}
        >
          <IconButton
            aria-label="previous"
            fontSize="24px"
            icon={<MdArrowBackIos />}
            onClick={slickPrev}
            variant="ghost"
            _hover={{ bg: "transparent" }}
          />
          <IconButton
            aria-label="next"
            fontSize="24px"
            icon={<MdArrowForwardIos />}
            onClick={slickNext}
            variant="ghost"
            _hover={{ bg: "transparent" }}
          />
        </HStack>
      </VStack>
    </Stack>
  );
};

export default AchievementComp;
